import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { PAGE_SIZE } from 'helper/values';
import useDebounce from 'hooks/useDebounce';

import { useGetBrandListingsQuery, useGetListingsQuery } from 'redux/slices/globalApi';

import { PROPERTY_RENTALS, SYNC_LISTINGS_STATUS } from '../constants';
import { toNumber } from '../utils';
import useSyncListingsStatusPolling from './useSyncListingsStatusPolling';

/**
 * Single hook for listings with search, infinite scroll, and polling.
 * - When brandUuid is provided: fetches that brand's listings (getBrandListings).
 * - When brandUuid is null/undefined: fetches all brands' listings (getListings).
 */
const useBrandListings = (brandUuid) => {
    const { integrationName } = useParams();

    const [listings, setListings] = useState([]);
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [pollingInterval, setPollingInterval] = useState(0);
    const [hasStartedListingsFetch, setHasStartedListingsFetch] = useState(false);

    const debouncedSearchTerm = useDebounce(searchTerm, 400);

    const isBrandScope = Boolean(brandUuid);

    const allListingsPayload = useMemo(
        () => ({
            searchTerms: debouncedSearchTerm ? [debouncedSearchTerm] : [],
            brandUuid: null,
            ignoreCurrentBrand: false,
            enabled: null,
        }),
        [debouncedSearchTerm]
    );

    const sync = useSyncListingsStatusPolling({
        integrationName,
        enabled: !isBrandScope,
        pollingMs: 5000,
    });

    // get particular brand listings for each brand page listing page
    const brandQuery = useGetBrandListingsQuery(
        {
            integrationName,
            brandUuid: brandUuid ?? '',
            page,
            filter: debouncedSearchTerm,
        },
        {
            skip: !isBrandScope,
            refetchOnMountOrArgChange: true,
            pollingInterval,
        }
    );

    // derive flags from sync status
    // track when to start listings fetching (once importedListings > 0)
    useEffect(() => {
        if (isBrandScope) return;
        if (hasStartedListingsFetch) return;

        const imported = Number(sync.statusData?.importedListings);
        if ((Number.isFinite(imported) && imported > 0) || sync.shouldFetchListings) {
            setHasStartedListingsFetch(true);
        }
    }, [
        isBrandScope,
        hasStartedListingsFetch,
        sync.statusData?.importedListings,
        sync.shouldFetchListings,
    ]);

    // get all brands total listing on default brand page
    const effectiveAllListingsPage = page;

    const allQuery = useGetListingsQuery(
        { integrationName, page: effectiveAllListingsPage, payload: allListingsPayload },
        {
            skip: isBrandScope || !hasStartedListingsFetch,
            refetchOnMountOrArgChange: true,
            pollingInterval,
        }
    );

    const showSyncProgress = sync.showSyncProgress;
    const syncProgress = sync.progressPercent;

    const data = isBrandScope ? brandQuery.data : allQuery.data;

    const isFetching = isBrandScope ? brandQuery.isFetching : allQuery.isFetching;

    const listingRef = useRef(null);

    // The listings api count wins whenever it is available:
    //  - once the sync status is `completed`, the listings api holds the final set
    //  - while the listings api keeps polling (no screen refresh), a `total` that
    //    differs from the status api `totalListings` means the listings api is the
    //    more accurate count
    // The status api counts are only a fallback until the listings api responds.
    const totalCount = useMemo(() => {
        const listingsTotal = toNumber(data?.total);

        if (listingsTotal !== null) return listingsTotal;

        const { status, importedListings } = sync?.statusData ?? {};

        const isSyncCompleted =
            typeof status === 'string' && status.toLowerCase() === SYNC_LISTINGS_STATUS.COMPLETED;

        return isSyncCompleted ? toNumber(importedListings) : null;
    }, [data?.total, sync?.statusData]);

    useEffect(() => {
        const disableConditionHostfully =
            integrationName === PROPERTY_RENTALS.HOSTFULLY && page > 1;
        const disableConditionDefault =
            integrationName !== PROPERTY_RENTALS.HOSTFULLY && data?.listings?.length > 0;
        if (disableConditionHostfully || disableConditionDefault) {
            setPollingInterval(0); // Disable polling after first page
        }

        const listingCountCondition =
            integrationName === PROPERTY_RENTALS.HOSTFULLY
                ? data?.listings?.length < PAGE_SIZE
                : data?.listings?.length === 0;
        if (page === 1 && listingCountCondition) {
            setPollingInterval(60000); // Enable polling for the first page
        }
        if (data) {
            setListings((prevListings) =>
                page === 1 ? data.listings : [...prevListings, ...data.listings]
            );
        }
    }, [data, page, integrationName]);

    const handleScroll = () => {
        const clientHeight = listingRef?.current?.clientHeight;
        const scrollTop = listingRef?.current?.scrollTop;
        const scrollHeight = listingRef?.current?.scrollHeight;

        const reachedEnd = clientHeight + scrollTop >= scrollHeight - 5;

        if (reachedEnd && !isFetching && data?.listings?.length > 0) {
            setPage((prev) => prev + 1);
        }
    };

    const onChange = (e) => {
        setSearchTerm((prevValue) => {
            //this is to reset the page if it was changed on scroll
            if (!prevValue || prevValue !== e.target.value) {
                setPage(1);
            }
            return e.target.value;
        });
    };

    const refetchListings = useCallback(() => {
        setPage(1);
        setListings([]);
    }, []);

    return {
        searchTerm,
        listingRef,
        listings,
        handleScroll,
        onChange,
        totalCount,
        isFetching,
        showSyncProgress,
        syncProgress,
        refetchListings,
    };
};

export default useBrandListings;
